import {
    Box,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { MouseEvent, useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import { useParkedContouringTool } from '~/components/toolbar/shared/useParkedContouringTool';
import { useVoiRegionRestriction } from '~/components/toolbar/shared/useVoiRegionRestriction';
import { WorldBoundsMm } from '~/domain/services/imageService/gridGeometry';
import { ContourTexture } from '~/engine/data/structure/ContourTexture';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import type { OperationOutcome } from '~/hooks/structure-set/operationOutcome';
import {
    EmptyCropRegionError,
    GlOperationBusyError,
    SDFOperations,
} from '~/engine/util/algorithms/sdf/SDFOperations';
import { CropDirection } from '~/engine/util/algorithms/sdf/SDFPostProcessing';
import { enqueueSnackbar } from '~/utils/notifications';
import { waitForUI } from '~/utils/uiUtils';
import { formatMm3AsCm3 } from '~/utils/units';
import { AUTO_PREVIEW_DELAY_MS } from '~/components/toolbar/OperationsToolbar/autoPreview';
import OperationApplyButton from '~/components/toolbar/OperationsToolbar/OperationApplyButton';
import ShowPreviewToggle from '~/components/toolbar/OperationsToolbar/ShowPreviewToggle';
import PreviewResultStatus from '~/components/toolbar/OperationsToolbar/PreviewResultStatus';
import RegionResetButton from '~/components/toolbar/OperationsToolbar/RegionResetButton';
import { usePostProcessingPreview } from '~/hooks/structure-set/usePostProcessingPreview';
import { useRoiSdfOperation } from '~/hooks/structure-set/useRoiSdfOperation';
import { useReportBusy } from '~/components/toolbar/OperationsToolbar/useReportBusy';

const DEFAULT_DIRECTION: CropDirection = 'keep-inside';

/** Identifies the settings an on-screen crop result belongs to. */
function cropPreviewKey(
    direction: CropDirection,
    bounds: WorldBoundsMm
): string {
    return [
        'crop',
        direction,
        bounds.minX,
        bounds.minY,
        bounds.minZ,
        bounds.maxX,
        bounds.maxY,
        bounds.maxZ,
    ].join(':');
}

/**
 * How long a crop waits for another GL operation to release the GPU
 * before reporting that it could not run, in ms. Past it the wait ends
 * with the underlying {@link GlOperationBusyError}, which the caller
 * states in words. A UI responsiveness figure only; it bounds no
 * clinical value.
 */
const GPU_CONTENTION_WAIT_MS = 5000;

/**
 * Crops to the box, waiting out a GPU held by another GL operation rather
 * than reading that refusal as a result. Rejects with an AbortError as
 * soon as the run is superseded, and with the underlying
 * {@link GlOperationBusyError} once the wait passes
 * {@link GPU_CONTENTION_WAIT_MS}.
 */
async function cropWaitingForGpu(
    srcTexture: ContourTexture,
    boundsMm: WorldBoundsMm,
    direction: CropDirection,
    signal: AbortSignal
): Promise<Awaited<ReturnType<typeof SDFOperations.applyCropToBounds>>> {
    const deadline = performance.now() + GPU_CONTENTION_WAIT_MS;
    for (;;) {
        try {
            return await SDFOperations.applyCropToBounds(
                srcTexture,
                boundsMm,
                direction,
                signal
            );
        } catch (error) {
            if (
                !(error instanceof GlOperationBusyError) ||
                performance.now() >= deadline
            ) {
                throw error;
            }
        }
        await waitForUI();
        if (signal.aborted) {
            // The same cancellation the compute itself raises when the
            // box moves under it, so a superseded run stays silent
            // instead of reporting the contention it was waiting on.
            throw new DOMException('Operation aborted', 'AbortError');
        }
    }
}

// ── Crop tab content ─────────────────────────────────────────────────

/**
 * Restricts the selected ROI to a draggable 3D box (keep only what is
 * inside), or clears whatever the box contains. The box is MANDATORY here
 * — there is no crop without a region, so `useVoiRegionRestriction` is
 * asked to force it on rather than offering it as an optional narrowing.
 */
export default function CropTab({
    onPreventCloseChange,
}: {
    onPreventCloseChange: (prevent: boolean) => void;
}) {
    const intl = useIntl();
    // The edit target's image: the box is drawn on the image the crop is
    // resolved against, which on the grid is the target's row, not a
    // globally selected series.
    const image = useActiveEditTarget().environment?.image?.parent;
    const { restrictToRegion, resetRegion, voiBounds } =
        useVoiRegionRestriction({
            consumer: 'post-processing-crop',
            imageUid: image?.dicomUid,
            imageGrid: image?.grid,
            // There is no crop without a region: the box is the
            // operation's domain, not an optional narrowing of it.
            mandatory: true,
        });
    // The box is draggable, and a press that misses a handle lands on
    // the image. While it is on screen the ROI-editing tools are parked, so
    // that press can only be a fumbled box drag, never a stroke on the
    // structure the crop is about to rewrite. Keyed on the restriction
    // being on rather than on this tab merely being mounted: the box, not
    // the tab, is what the user can hit.
    useParkedContouringTool(restrictToRegion);
    const [direction, setDirection] =
        useState<CropDirection>(DEFAULT_DIRECTION);
    const { isProcessing, executeOperation } = useRoiSdfOperation();
    const {
        isPreviewing,
        isPreviewReady,
        isPreviewVisible,
        setPreviewVisible,
        preview,
        clearPreview,
        sdfRevision,
    } = usePostProcessingPreview();
    // True once a preview has run and found the box removes nothing — a
    // keep-inside box that already contains the whole structure. The
    // result is then the structure unchanged, which is the same picture as
    // no preview at all.
    const [previewFoundNothing, setPreviewFoundNothing] = useState(false);
    // True once a preview has run and the box held no part of the
    // structure. A keep-inside crop REFUSES that box rather than emptying
    // the structure, so there is no result to draw and nothing for Apply
    // to commit — the panel says so in place of the result line, quietly:
    // pausing a drag off the anatomy is an ordinary mid-gesture state, not
    // a failure to report as one.
    const [previewFoundEmptyRegion, setPreviewFoundEmptyRegion] =
        useState(false);

    useReportBusy(isProcessing, onPreventCloseChange);

    const handleDirectionChange = (
        _event: MouseEvent<HTMLElement>,
        value: CropDirection | null
    ) => {
        // ToggleButtonGroup emits null when the active button is
        // re-clicked; ignore it so a direction is always selected.
        if (value !== null) {
            setDirection(value);
        }
    };

    /**
     * The preview's reading of the two refusals {@link
     * SDFOperations.applyCropToBounds} raises. Both are ordinary states of
     * a box being dragged, not failures of the application: a box paused
     * off the structure is a reposition — keep-inside is the default
     * direction, so an ordinary mid-drag pause reaches it — and a GPU
     * still held past the wait is contention. Neither may reach the shared
     * preview hook's catch-all, which would throw the engine's
     * untranslated message at the clinician as "Preview failed: …", once
     * per pause, and tear the overlay down with it.
     *
     * Both are answered with no result: nothing is drawn, Apply stays
     * gated, and the panel says which state it is in.
     */
    const previewCrop = useCallback(
        async (
            srcTexture: ContourTexture,
            boundsMm: WorldBoundsMm,
            cropDirection: CropDirection,
            signal: AbortSignal
        ): Promise<
            Awaited<ReturnType<typeof SDFOperations.applyCropToBounds>>
        > => {
            try {
                return await cropWaitingForGpu(
                    srcTexture,
                    boundsMm,
                    cropDirection,
                    signal
                );
            } catch (error) {
                // Gated on the signal for the same reason the result is:
                // a superseded run must not state anything over the run
                // that replaced it.
                if (signal.aborted) {
                    throw error;
                }
                if (error instanceof EmptyCropRegionError) {
                    setPreviewFoundEmptyRegion(true);
                    return undefined;
                }
                if (error instanceof GlOperationBusyError) {
                    // The wait above absorbs transient contention
                    // silently, so reaching here means the GPU was held
                    // for the whole ceiling — worth one translated line,
                    // since the preview the user is waiting for will not
                    // arrive on its own.
                    enqueueSnackbar(
                        intl.formatMessage({
                            id: 'postProcessing.crop.gpuBusy',
                        }),
                        { variant: 'warning' }
                    );
                    return undefined;
                }
                throw error;
            }
        },
        [intl]
    );

    // The box is the parameter, so this tab previews on a debounce rather
    // than on a button — and the box moves continuously while the user
    // drags it. The overlay is DISPOSED at the top of every run rather
    // than merely marked not-ready: this operation REMOVES contour, so a
    // result held over from the box's previous position shows contour
    // missing where the box no longer is. The structure re-appearing
    // un-cropped mid-drag is the honest state.
    useEffect(() => {
        if (isProcessing) {
            return;
        }
        clearPreview();
        setPreviewFoundNothing(false);
        setPreviewFoundEmptyRegion(false);
        // null bounds are NOT READY, never "unrestricted" — see the
        // module doc. Skipping the preview here (rather than computing
        // one against no box) keeps that invariant on the preview path
        // too, not just Apply.
        if (voiBounds === null) {
            return;
        }
        const timeout = setTimeout(() => {
            preview(
                async (srcTexture, signal) => {
                    const result = await previewCrop(
                        srcTexture,
                        voiBounds,
                        direction,
                        signal
                    );
                    // A keep-inside box that already contains everything is a
                    // no-op, and looks exactly like one that was never
                    // previewed. Gated on the signal, which this tab trips
                    // more often than any other since dragging the box
                    // auto-previews.
                    if (!signal.aborted) {
                        setPreviewFoundNothing(result?.clearedVoxelCount === 0);
                    }
                    if (!result) {
                        return { status: 'failed' };
                    }
                    // applyCropToBounds allocates a texture either way,
                    // where the tagged outcome's 'unchanged' arm carries
                    // none — so a crop that cleared nothing releases the
                    // texture here rather than handing over one the
                    // preview would never take ownership of.
                    if (result.clearedVoxelCount === 0) {
                        result.texture.delete();
                        return { status: 'unchanged' };
                    }
                    return { status: 'changed', texture: result.texture };
                },
                cropPreviewKey(direction, voiBounds),
                {
                    hidePreviewedStructure: true,
                }
            );
        }, AUTO_PREVIEW_DELAY_MS);
        return () => clearTimeout(timeout);
    }, [
        voiBounds,
        direction,
        isProcessing,
        sdfRevision,
        preview,
        clearPreview,
        previewCrop,
    ]);

    const runOperation = useCallback(
        async (roi: RegionOfInterest): Promise<OperationOutcome> => {
            // Re-checked here, not just at the Apply-button gate: a click
            // handler must never sample a null box as "no restriction" —
            // an unrestricted crop is a no-op under keep-inside and
            // destructive under delete-inside.
            if (voiBounds === null) {
                return { status: 'failed' };
            }
            // EmptyCropRegionError propagates uncaught: useRoiSdfOperation's
            // resolveFailureMessageId (below) maps it to its own message,
            // so it must not be caught and reported here too — doing both
            // would stack two contradictory snackbars for one failure.
            const result = await SDFOperations.applyCropToBounds(
                roi.texture!,
                voiBounds,
                direction
            );
            if (!result) {
                return { status: 'failed' };
            }
            // Voxels are an engine unit and never reach the user: the
            // count is converted here, at the UI boundary, using the
            // spacings of the very grid the crop counted on. The toast
            // states cm³ like every other clinical volume in the app
            // (see docs/ui_specs.md), and formatMm3AsCm3 states a bound
            // rather than rounding a non-zero removal down to "0.00".
            const grid = roi.texture!.textureGrid;
            const voxelVolumeMm3 = grid.resX * grid.resY * grid.resZ;
            // A crop that cleared nothing is not a completed crop, and
            // "removed 0.00 cm³" does not say why. Which no-op it was
            // follows from the direction: deleting inside a box that holds
            // none of the structure never met it, while keeping the inside
            // of a box that holds all of it had nothing to drop.
            const outcome =
                result.clearedVoxelCount > 0
                    ? 'cropped'
                    : direction === 'delete-inside'
                      ? 'noOverlap'
                      : 'entirelyInside';
            const successValues = {
                outcome,
                removed: formatMm3AsCm3(
                    result.clearedVoxelCount * voxelVolumeMm3
                ),
            };
            // A box that already contains the whole structure clears
            // nothing: the operation's own voxel count, not the picture,
            // is what says so, and an edit that removed no contour must
            // not take an undo step. The texture is released here because
            // the 'unchanged' arm carries none — applyCropToBounds
            // allocates one whether or not it cleared anything.
            if (result.clearedVoxelCount === 0) {
                result.texture.delete();
                return { status: 'unchanged', successValues };
            }
            return {
                status: 'changed',
                texture: result.texture,
                successValues,
            };
        },
        [voiBounds, direction]
    );

    // EmptyCropRegionError needs its OWN message ("move the box"), distinct
    // from the generic failedMessageId ("operation failed") — an
    // off-anatomy box is a reposition, not a failure. Resolved here rather
    // than caught-and-rethrown in runOperation, so exactly one snackbar
    // reaches the user instead of the specific one stacking on top of the
    // hook's generic catch-all.
    const resolveFailureMessageId = useCallback((error: unknown) => {
        if (error instanceof EmptyCropRegionError) {
            return 'postProcessing.crop.emptyRegion';
        }
        // The apply path runs the crop ONCE — it must not spin waiting for
        // the GPU while the busy overlay is up and the structure is being
        // rewritten. So contention is stated as what it is, "the GPU is
        // busy, try again", rather than merged into the generic "crop
        // failed" that says nothing about what to do next.
        if (error instanceof GlOperationBusyError) {
            return 'postProcessing.crop.gpuBusy';
        }
        return undefined;
    }, []);

    const handleApply = () => {
        clearPreview();
        setPreviewFoundNothing(false);
        setPreviewFoundEmptyRegion(false);
        executeOperation({
            operationFn: runOperation,
            operationName: 'Crop To Region',
            busyMessageId: 'postProcessing.crop.busy',
            successMessageId: 'postProcessing.crop.success',
            failedMessageId: 'postProcessing.crop.failed',
            resolveFailureMessageId,
            logLabel: 'Crop to region',
        });
    };

    return (
        <Box display="flex" flexDirection="column" gap={1.5}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                }}
            >
                <Typography variant="body2" color="text.secondary">
                    <FormattedMessage id="postProcessing.crop.hint" />
                </Typography>
                {/* The region is mandatory here, so there is always one to
                    reset — and it is the only way back from a box dragged
                    off-slab, where nothing is left on screen to grab. */}
                <RegionResetButton
                    onReset={resetRegion}
                    disabled={isProcessing}
                />
            </Box>
            <ToggleButtonGroup
                value={direction}
                exclusive
                onChange={handleDirectionChange}
                size="small"
                disabled={isProcessing}
                // Names what the GROUP chooses, not the tab it sits in:
                // the panel header already says which operation this is,
                // and "Crop" told a screen reader nothing about the
                // keep-or-delete decision these two buttons carry.
                aria-label={intl.formatMessage({
                    id: 'postProcessing.crop.direction',
                })}
            >
                <ToggleButton value="keep-inside">
                    <FormattedMessage id="postProcessing.crop.keepInside" />
                </ToggleButton>
                <ToggleButton value="delete-inside">
                    <FormattedMessage id="postProcessing.crop.deleteInside" />
                </ToggleButton>
            </ToggleButtonGroup>
            <ShowPreviewToggle
                visible={isPreviewVisible}
                onChange={setPreviewVisible}
                disabled={!isPreviewReady}
            />
            <OperationApplyButton
                onApply={handleApply}
                isPreviewing={isPreviewing}
                // The tab's own precondition on top of preview readiness:
                // Apply must never sample a null box as "unrestricted",
                // because an unrestricted delete-inside would clear the
                // structure.
                applyDisabled={voiBounds === null}
                isPreviewReady={isPreviewReady}
                isProcessing={isProcessing}
            />
            <PreviewResultStatus
                isPreviewOnScreen={isPreviewReady}
                previewChangesNothing={previewFoundNothing}
            />
            {previewFoundEmptyRegion && (
                <Typography variant="body2" color="text.secondary">
                    <FormattedMessage id="postProcessing.crop.previewEmptyRegion" />
                </Typography>
            )}
        </Box>
    );
}
